/**
 * Shared DOM utilities used by tour ``onBeforeNext`` / ``autoAdvanceWhen``
 * hooks.
 *
 * Each helper here is *generic* (works for any tour). Tour-specific
 * selectors and predicates (e.g. "is the panorama dialog open?") should
 * live alongside the tour that uses them.
 *
 * All helpers tolerate "element not present" silently — tours shouldn't
 * crash if the page changed; they should just degrade gracefully and let
 * the tour's ``skipIf`` / ``waitForCondition`` chain take over.
 */

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

/** ``document.querySelector`` with an SSR / DOM-less safety check. */
export const q = <T extends Element>(sel: string): T | null =>
  typeof document === 'undefined' ? null : document.querySelector<T>(sel);

/** Sugar for ``Boolean(q(sel))``. */
export const has = (sel: string): boolean => Boolean(q(sel));

// ---------------------------------------------------------------------------
// Synthetic clicks
// ---------------------------------------------------------------------------

/**
 * Dispatch a "real" click on an element.
 *
 * We dispatch the full pointer + mouse + click sequence with proper
 * coordinates because:
 *
 *   1. Radix UI primitives (Select trigger, Select option) listen on
 *      ``pointerdown`` / ``pointerup`` — a plain ``click`` doesn't open
 *      the popover or pick an option.
 *   2. Some downstream React handlers read ``event.clientX/Y`` or rely on
 *      ``event.currentTarget``, both of which need a real-looking event.
 *
 * Coordinates are pulled from the element's bounding rect so the
 * dispatched event roughly matches a click at the visual center of the
 * target.
 */
export function realClick(el: Element): void {
  const rect = el.getBoundingClientRect();
  const cx = Math.round(rect.left + rect.width / 2);
  const cy = Math.round(rect.top + rect.height / 2);
  const base = {
    bubbles: true,
    cancelable: true,
    composed: true,
    button: 0,
    buttons: 1,
    clientX: cx,
    clientY: cy,
    screenX: cx,
    screenY: cy,
    view: window,
  };
  const pointerInit: PointerEventInit = {
    ...base,
    pointerType: 'mouse',
    isPrimary: true,
  };
  const mouseInit: MouseEventInit = base;

  // Pointer first (Radix), then mouse (some libraries differentiate),
  // then the actual click.
  try {
    el.dispatchEvent(new PointerEvent('pointerdown', pointerInit));
  } catch {
    /* PointerEvent unavailable — older browser; mouse events below cover it. */
  }
  el.dispatchEvent(new MouseEvent('mousedown', mouseInit));
  try {
    el.dispatchEvent(new PointerEvent('pointerup', pointerInit));
  } catch {
    /* see above */
  }
  el.dispatchEvent(new MouseEvent('mouseup', mouseInit));
  el.dispatchEvent(new MouseEvent('click', mouseInit));
}

/**
 * Invoke an element's React ``onClick`` prop directly with a fully-formed
 * synthetic event.
 *
 * Why we need this: dispatching a native click and letting React's
 * delegated event system pick it up *does* fire the React handler, but
 * ``event.currentTarget`` isn't reliably set on the resulting synthetic
 * event for SVG / portaled / non-trusted dispatches. Some downstream
 * handlers read ``event.currentTarget.getBoundingClientRect()`` (the
 * photo-marker click chain does), and a missing currentTarget makes them
 * throw silently.
 *
 * Calling ``props.onClick(event)`` directly sidesteps delegation entirely:
 * we construct an event object that has every field React handlers
 * commonly read and feed it straight in.
 *
 * Returns ``false`` if React props can't be located or there's no
 * ``onClick`` handler — callers should fall back to ``realClick``.
 */
export function callReactOnClick(el: Element): boolean {
  // React 17+ stashes props on the DOM node as ``__reactProps$<id>``.
  // The suffix is per-renderer so we have to scan keys.
  const propsKey = Object.keys(el).find((k) => k.startsWith('__reactProps$'));
  if (!propsKey) return false;
  const props = (el as unknown as Record<string, unknown>)[propsKey] as
    | { onClick?: (event: unknown) => void }
    | undefined;
  if (!props || typeof props.onClick !== 'function') return false;

  const rect = el.getBoundingClientRect();
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;

  // A React.MouseEvent-shaped object. Includes every field tour-driven
  // handler chains (markers, panorama, hotspots) are known to read.
  const fakeEvent = {
    type: 'click',
    bubbles: true,
    cancelable: true,
    defaultPrevented: false,
    eventPhase: 2, // AT_TARGET
    timeStamp: performance.now(),
    target: el,
    currentTarget: el,
    button: 0,
    buttons: 0,
    clientX: cx,
    clientY: cy,
    pageX: cx,
    pageY: cy,
    screenX: cx,
    screenY: cy,
    shiftKey: false,
    ctrlKey: false,
    metaKey: false,
    altKey: false,
    stopPropagation: () => {},
    preventDefault: () => {},
    persist: () => {},
    isDefaultPrevented: () => false,
    isPropagationStopped: () => false,
    nativeEvent: {
      isTrusted: false,
      type: 'click',
      target: el,
      currentTarget: el,
    },
  };

  props.onClick(fakeEvent);
  return true;
}

/**
 * Click an element preferring its React ``onClick`` over a dispatched
 * native event. Falls back to {@link realClick} if the element has no
 * React ``onClick`` (e.g. Radix Select primitives, which run their
 * selection logic from ``onPointerDown``).
 *
 * Use this for "regular" interactive elements (FAB buttons, tab buttons,
 * marker SVG nodes, popover rows). Use {@link realClick} or
 * {@link clickAndWaitFor} for Radix Select trigger / option.
 */
export const clickPreferReact = (sel: string): void => {
  const el = q(sel);
  if (!el) return;
  if (!callReactOnClick(el)) realClick(el);
};

// ---------------------------------------------------------------------------
// Waiting / sequencing
// ---------------------------------------------------------------------------

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * Click ``clickSelector`` (via {@link realClick}), then poll for
 * ``waitForSelector`` to appear.
 *
 * Useful when an ``onBeforeNext`` opens a popover that the next step's
 * spotlight will target — we don't want to advance until the new element
 * is in the DOM.
 */
export async function clickAndWaitFor(
  clickSelector: string,
  waitForSelector: string,
  timeoutMs = 1500,
): Promise<void> {
  const target = q(clickSelector);
  if (!target) return;
  realClick(target);
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    if (q(waitForSelector)) return;
    await sleep(40);
  }
}

/**
 * Poll a predicate until it returns true or ``timeoutMs`` elapses.
 *
 * Used by ``onBeforeNext`` hooks to wait for an async DOM transition (a
 * Radix Dialog mounting, a tooltip rendering, a tab swapping) so the next
 * step's ``skipIf`` evaluates against the post-transition state.
 *
 * The initial 100 ms delay gives React's scheduler time to commit any
 * pending state updates from a click before we sample the DOM.
 *
 * Returning instead of throwing on timeout is intentional: a downstream
 * ``skipIf`` handles "this still didn't happen" the same way it handles a
 * legitimate skip. ``label`` is logged on dev-mode timeouts so it's
 * obvious which transition didn't fire.
 */
export async function waitForCondition(
  predicate: () => boolean,
  timeoutMs = 4000,
  label?: string,
): Promise<boolean> {
  await sleep(100);
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    if (predicate()) return true;
    await sleep(50);
  }
  if (import.meta.env.DEV && label) {
    // eslint-disable-next-line no-console
    console.warn(`[tours] waitForCondition timed out: ${label}`);
  }
  return false;
}

// ---------------------------------------------------------------------------
// Keyboard
// ---------------------------------------------------------------------------

/**
 * Dispatch a synthetic Escape ``keydown`` to close the topmost open Radix
 * Dialog / Select / etc.
 *
 * The TourOverlay's keydown handler ignores synthetic events
 * (``e.isTrusted === false``), so this won't accidentally dismiss the tour.
 */
export function pressEscape(): void {
  document.dispatchEvent(
    new KeyboardEvent('keydown', { key: 'Escape', bubbles: true, cancelable: true }),
  );
}
